import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";

type Range = { min: number; max: number };
type Resolution = { width: number; height: number; display: string };

type DeviceInfo = {
  manufacturer?: string;
  model?: string;
  firmwareVersion?: string;
  serialNumber?: string;
  hardwareId?: string;
  macAddress?: string;
  macAddresses?: string[];
};

type VideoSettings = {
  resolution: Resolution;
  fps: number;
  bitrateKbps: number;
  quality: number;
  encodingInterval?: number;
};

type EditableCapabilities = {
  resolution: boolean;
  fps: boolean;
  bitrateKbps: boolean;
  quality: boolean;
};

type VideoProfile = {
  profileToken: string;
  profileName: string;
  configurationToken: string;
  encoding: string;
  profileSelectable?: boolean;
  writable: boolean;
  editable?: Partial<EditableCapabilities>;
  capabilityMessages?: Partial<Record<keyof EditableCapabilities, string>>;
  current: VideoSettings;
  options: {
    resolutions: Resolution[];
    fps: Range | null;
    fpsValues?: number[];
    bitrateKbps: Range | null;
    bitrateKbpsValues?: number[];
    quality: Range | null;
    qualityValues?: number[];
    encodingInterval?: Range | null;
  };
  warnings?: string[];
};

type Camera = {
  cameraId: string;
  enabled?: boolean;
  onvif?: { profileToken?: string };
  updatedAt?: string;
  deviceDetails?: DeviceInfo;
  planType?: string;
  planName?: string;
  billingModel?: string;
  billingMasterPlanId?: string;
  billingRetailPlanId?: string;
  billingPlanVersion?: string;
  billingPlanAssignedAt?: string;
  [key: string]: unknown;
};

type Props = {
  camera: Camera;
  onUpdateCamera: (camera: any) => void;
  onDeviceInfo?: (deviceInfo: DeviceInfo) => void;
};

type Draft = {
  resolution: Resolution;
  fps: number;
  bitrateKbps: number;
  quality: number;
};

async function getIdTokenOrThrow() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Your authentication session expired. Sign in again.");
  return token;
}

async function readResponse(response: Response) {
  const text = await response.text();
  try {
    return { text, json: text ? JSON.parse(text) : null };
  } catch {
    return { text, json: null };
  }
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Unexpected error";
}

function draftFromProfile(profile: VideoProfile): Draft {
  return {
    resolution: { ...profile.current.resolution },
    fps: Number(profile.current.fps),
    bitrateKbps: Number(profile.current.bitrateKbps),
    quality: Number(profile.current.quality),
  };
}

function settingsEqual(profile: VideoProfile, draft: Draft) {
  return (
    profile.current.resolution.width === draft.resolution.width &&
    profile.current.resolution.height === draft.resolution.height &&
    Number(profile.current.fps) === Number(draft.fps) &&
    Number(profile.current.bitrateKbps) === Number(draft.bitrateKbps) &&
    Math.abs(Number(profile.current.quality) - Number(draft.quality)) < 0.001
  );
}

function normalizeSupportedValue(
  value: number,
  range: Range | null,
  values?: number[],
) {
  if (Array.isArray(values) && values.length > 0) {
    if (!Number.isFinite(value)) return values[0];
    return values.reduce((nearest, candidate) =>
      Math.abs(candidate - value) < Math.abs(nearest - value) ? candidate : nearest,
    );
  }
  if (!range) return value;
  if (!Number.isFinite(value)) return range.min;
  return Math.min(range.max, Math.max(range.min, value));
}

function isFixedPlanQualityLocked(camera: Camera) {
  const planType = String(camera.planType || "").trim().toLowerCase();
  const billingModel = String(camera.billingModel || "").trim().toUpperCase();
  const planName = String(camera.planName || "").trim().toLowerCase();

  if (
    planType === "payg" ||
    billingModel === "PAYG" ||
    billingModel.startsWith("PAYG_")
  ) {
    return false;
  }

  const hasNamedPlan =
    Boolean(planName) &&
    !["no plan", "none", "unassigned"].includes(planName);

  return (
    ["cloud_3_day", "cloud_7_day", "partner"].includes(planType) ||
    hasNamedPlan ||
    Boolean(
      billingModel ||
        camera.billingMasterPlanId ||
        camera.billingRetailPlanId ||
        camera.billingPlanVersion ||
        camera.billingPlanAssignedAt,
    )
  );
}

export default function VideoQualityCapabilitiesTab({
  camera,
  onUpdateCamera,
  onDeviceInfo,
}: Props) {
  const fixedPlanLocked = isFixedPlanQualityLocked(camera);
  const fixedPlanName = String(camera.planName || "").trim() || "The fixed billing plan";
  const onDeviceInfoRef = useRef(onDeviceInfo);
  useEffect(() => {
    onDeviceInfoRef.current = onDeviceInfo;
  }, [onDeviceInfo]);

  const [profiles, setProfiles] = useState<VideoProfile[]>([]);
  const [selectedProfileToken, setSelectedProfileToken] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [streamUri, setStreamUri] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);
  const [deviceWarnings, setDeviceWarnings] = useState<string[]>([]);

  const selectedProfile = useMemo(
    () =>
      profiles.find((profile) => profile.profileToken === selectedProfileToken) ||
      null,
    [profiles, selectedProfileToken],
  );

  const loadSettings = useCallback(
    async (preferredProfileToken?: string, keepStatus = false) => {
      setLoading(true);
      if (!keepStatus) {
        setStatus("Reading encoder capabilities and device information from the camera...");
        setStatusIsError(false);
      }
      try {
        const idToken = await getIdTokenOrThrow();
        const response = await fetch(
          `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/video-settings`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${idToken}`,
            },
          },
        );
        const { text, json } = await readResponse(response);
        if (!response.ok) {
          throw new Error(
            `${response.status}: ${
              json?.error || json?.message || text || response.statusText ||
              "Unable to load video settings."
            }`,
          );
        }

        const returnedProfiles = Array.isArray(json?.profiles)
          ? (json.profiles as VideoProfile[])
          : [];
        if (!returnedProfiles.length) {
          throw new Error("The camera returned no ONVIF video profiles.");
        }

        const requestedToken =
          preferredProfileToken ||
          json?.selectedProfileToken ||
          camera.onvif?.profileToken;
        const selected =
          returnedProfiles.find(
            (profile) => profile.profileToken === requestedToken,
          ) || returnedProfiles[0];

        const deviceInfo = (json?.deviceInfo || {}) as DeviceInfo;
        if (Object.values(deviceInfo).some(Boolean)) {
          onDeviceInfoRef.current?.(deviceInfo);
        }

        setProfiles(returnedProfiles);
        setSelectedProfileToken(selected.profileToken);
        setDraft(draftFromProfile(selected));
        setStreamUri(String(json?.streamUri || ""));
        setDeviceWarnings(Array.isArray(json?.warnings) ? json.warnings : []);
        if (!keepStatus) {
          if (fixedPlanLocked) {
            setStatus(
              `Loaded ${returnedProfiles.length} ONVIF stream${
                returnedProfiles.length === 1 ? "" : "s"
              }. ${fixedPlanName} controls these quality settings, so they are read-only.`,
            );
          } else {
            const editableCount = Object.values(selected.editable || {}).filter(Boolean)
              .length;
            setStatus(
              `Loaded ${returnedProfiles.length} ONVIF stream${
                returnedProfiles.length === 1 ? "" : "s"
              }. ${editableCount} encoder field${
                editableCount === 1 ? " is" : "s are"
              } editable for the selected stream.`,
            );
          }
        }
      } catch (error) {
        setProfiles([]);
        setSelectedProfileToken("");
        setDraft(null);
        setStreamUri("");
        setDeviceWarnings([]);
        setStatusIsError(true);
        setStatus(`Unable to load video settings: ${errorMessage(error)}`);
      } finally {
        setLoading(false);
      }
    },
    [
      camera.cameraId,
      camera.onvif?.profileToken,
      fixedPlanLocked,
      fixedPlanName,
    ],
  );

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  function selectProfile(profileToken: string) {
    const profile = profiles.find((item) => item.profileToken === profileToken);
    setSelectedProfileToken(profileToken);
    setDraft(profile ? draftFromProfile(profile) : null);
    setStatus("");
    setStatusIsError(false);
  }

  function updateDraft(changes: Partial<Draft>) {
    setDraft((current) => (current ? { ...current, ...changes } : current));
    setStatus("");
    setStatusIsError(false);
  }

  const profileChanged =
    Boolean(selectedProfileToken) &&
    selectedProfileToken !== String(camera.onvif?.profileToken || "");
  const encoderChanged =
    Boolean(selectedProfile && draft) &&
    !settingsEqual(selectedProfile as VideoProfile, draft as Draft);
  const hasChanges = !fixedPlanLocked && (profileChanged || encoderChanged);

  async function saveSettings() {
    if (!selectedProfile || !draft || saving) return;
    if (fixedPlanLocked) {
      setStatusIsError(false);
      setStatus(`${fixedPlanName} controls these quality settings. Change the fixed plan to apply different values.`);
      return;
    }
    if (profileChanged && selectedProfile.profileSelectable === false) {
      setStatusIsError(true);
      setStatus("This camera profile cannot be selected for streaming.");
      return;
    }

    const nextDraft: Draft = {
      resolution: draft.resolution,
      fps: normalizeSupportedValue(
        Number(draft.fps),
        selectedProfile.options.fps,
        selectedProfile.options.fpsValues,
      ),
      bitrateKbps: Math.round(
        normalizeSupportedValue(
          Number(draft.bitrateKbps),
          selectedProfile.options.bitrateKbps,
          selectedProfile.options.bitrateKbpsValues,
        ),
      ),
      quality: normalizeSupportedValue(
        Number(draft.quality),
        selectedProfile.options.quality,
        selectedProfile.options.qualityValues,
      ),
    };
    setDraft(nextDraft);
    setSaving(true);
    setStatusIsError(false);
    setStatus(
      camera.enabled === false
        ? "Applying ONVIF encoder settings..."
        : "Stopping ingestion, applying ONVIF settings, and restarting the selected stream...",
    );

    try {
      const idToken = await getIdTokenOrThrow();
      const response = await fetch(
        `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/video-settings`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            profileToken: selectedProfile.profileToken,
            settings: {
              resolution: {
                width: nextDraft.resolution.width,
                height: nextDraft.resolution.height,
              },
              fps: nextDraft.fps,
              bitrateKbps: nextDraft.bitrateKbps,
              quality: nextDraft.quality,
            },
          }),
        },
      );
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          `${response.status}: ${
            json?.error || json?.message || text || response.statusText ||
            "Unable to save video settings."
          }`,
        );
      }

      onUpdateCamera({
        ...camera,
        ...(json?.camera || {}),
        onvif: {
          ...((json?.camera || camera).onvif || {}),
          profileToken: selectedProfile.profileToken,
        },
        updatedAt: json?.camera?.updatedAt || new Date().toISOString(),
      });

      await loadSettings(selectedProfile.profileToken, true);
      setStatusIsError(false);
      setStatus(
        json?.lifecycle?.restarted
          ? "Camera settings saved. MediaMTX ingestion restarted on the selected ONVIF stream."
          : "Camera settings saved successfully.",
      );
    } catch (error) {
      setStatusIsError(true);
      setStatus(`Unable to save video settings: ${errorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  }

  const editable = selectedProfile?.editable || {};
  const saveDisabled =
    fixedPlanLocked ||
    loading ||
    saving ||
    !selectedProfile ||
    !draft ||
    !hasChanges;

  return (
    <section style={panelStyle}>
      <div style={bannerStyle}>
        <div>
          <strong style={{ display: "block", color: "#1e3a8a" }}>
            ONVIF Camera Quality
          </strong>
          <span style={{ color: "#31539c" }}>
            Qumra enables only the capacities actually reported by the selected
            camera stream.
          </span>
        </div>
        <button
          type="button"
          onClick={() => void loadSettings(selectedProfileToken)}
          disabled={loading || saving}
          style={secondaryButtonStyle}
        >
          {loading ? "Reading..." : "Reload from Camera"}
        </button>
      </div>

      {fixedPlanLocked && (
        <div role="status" style={planLockStyle}>
          <div>
            <strong style={{ display: "block", color: "#334155" }}>
              Quality managed by fixed plan
            </strong>
            <span>
              {fixedPlanName} controls the stream, resolution, FPS, image quality,
              and bitrate. The assigned values remain visible below but cannot be changed.
            </span>
          </div>
          <span style={lockedBadgeStyle}>Read-only</span>
        </div>
      )}

      {selectedProfile && draft ? (
        <fieldset
          aria-label="Camera quality settings"
          data-plan-locked={fixedPlanLocked ? "true" : "false"}
          disabled={fixedPlanLocked}
          style={{
            ...formStyle,
            ...(fixedPlanLocked ? lockedFormStyle : {}),
          }}
        >
          <Field label="Stream channel">
            <select
              aria-label="Stream channel"
              value={selectedProfileToken}
              onChange={(event) => selectProfile(event.target.value)}
              disabled={saving}
              style={selectStyle}
            >
              {profiles.map((profile) => (
                <option key={profile.profileToken} value={profile.profileToken}>
                  {profile.profileName} ({profile.profileToken}) · {profile.encoding}
                </option>
              ))}
            </select>
          </Field>

          <FieldWithHint
            label="Resolution"
            hint={
              editable.resolution
                ? "Camera-reported resolutions"
                : selectedProfile.capabilityMessages?.resolution
            }
          >
            <select
              aria-label="Resolution"
              value={`${draft.resolution.width}x${draft.resolution.height}`}
              onChange={(event) => {
                const next = selectedProfile.options.resolutions.find(
                  (item) => item.display === event.target.value,
                );
                if (next) updateDraft({ resolution: next });
              }}
              disabled={saving || !editable.resolution}
              style={selectStyle}
            >
              {selectedProfile.options.resolutions.map((resolution) => (
                <option key={resolution.display} value={resolution.display}>
                  {resolution.width} × {resolution.height}
                </option>
              ))}
            </select>
          </FieldWithHint>

          <CapabilityRangeField
            label="FPS"
            range={selectedProfile.options.fps}
            values={selectedProfile.options.fpsValues}
            value={draft.fps}
            unit="frames per second"
            disabled={saving || !editable.fps}
            reason={selectedProfile.capabilityMessages?.fps}
            onChange={(value) => updateDraft({ fps: value })}
          />

          <CapabilityRangeField
            label="Image quality"
            range={selectedProfile.options.quality}
            values={selectedProfile.options.qualityValues}
            value={draft.quality}
            unit="quality"
            step={0.1}
            disabled={saving || !editable.quality}
            reason={selectedProfile.capabilityMessages?.quality}
            onChange={(value) => updateDraft({ quality: value })}
          />

          <CapabilityRangeField
            label="Bitrate"
            range={selectedProfile.options.bitrateKbps}
            values={selectedProfile.options.bitrateKbpsValues}
            value={draft.bitrateKbps}
            unit="kbit/s"
            disabled={saving || !editable.bitrateKbps}
            reason={selectedProfile.capabilityMessages?.bitrateKbps}
            onChange={(value) => updateDraft({ bitrateKbps: Math.round(value) })}
          />

          {selectedProfile.warnings?.length ? (
            <div role="alert" style={warningStyle}>
              {selectedProfile.warnings.join(" ")}
            </div>
          ) : null}

          <div style={summaryGridStyle}>
            <Summary label="Selected stream" value={selectedProfile.profileName} />
            <Summary
              label="Resolution"
              value={`${draft.resolution.width} × ${draft.resolution.height}`}
            />
            <Summary label="Frame rate" value={`${draft.fps} FPS`} />
            <Summary label="Bitrate" value={`${draft.bitrateKbps} kbit/s`} />
          </div>

          {streamUri && (
            <div style={streamUriStyle} title={streamUri}>
              Selected RTSP source: {streamUri}
            </div>
          )}

          {deviceWarnings.length > 0 && (
            <div style={noteStyle}>{deviceWarnings.join(" ")}</div>
          )}
        </fieldset>
      ) : (
        !loading && (
          <div style={emptyStyle}>
            Reload the camera to retrieve ONVIF encoder capabilities.
          </div>
        )
      )}

      {status && (
        <div
          role={statusIsError ? "alert" : "status"}
          style={messageStyle(statusIsError)}
        >
          {status}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          type="button"
          onClick={() => void saveSettings()}
          disabled={saveDisabled}
          style={primaryButtonStyle(saveDisabled)}
        >
          {fixedPlanLocked
            ? "Managed by Fixed Plan"
            : saving
              ? "Applying & Restarting..."
              : "Save & Restart Stream"}
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={rowStyle}>
      <strong style={labelStyle}>{label}</strong>
      <div>{children}</div>
    </div>
  );
}

function FieldWithHint({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={rowStyle}>
      <div>
        <strong style={labelStyle}>{label}</strong>
        {hint && <div style={hintStyle}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function CapabilityRangeField({
  label,
  range,
  values,
  value,
  unit,
  step = 1,
  disabled,
  reason,
  onChange,
}: {
  label: string;
  range: Range | null;
  values?: number[];
  value: number;
  unit: string;
  step?: number;
  disabled: boolean;
  reason?: string;
  onChange: (value: number) => void;
}) {
  const exactValues = Array.isArray(values)
    ? Array.from(new Set(values.map(Number).filter(Number.isFinite))).sort(
        (a, b) => a - b,
      )
    : [];
  const usesExactValues = exactValues.length > 1;
  const selectedIndex = usesExactValues
    ? Math.max(
        0,
        exactValues.findIndex(
          (candidate) => Math.abs(candidate - value) < 0.001,
        ),
      )
    : 0;
  const min = usesExactValues ? exactValues[0] : range?.min ?? value;
  const max = usesExactValues
    ? exactValues[exactValues.length - 1]
    : range?.max ?? value;

  return (
    <div style={rowStyle}>
      <div>
        <strong style={labelStyle}>{label}</strong>
        {usesExactValues ? (
          <div style={hintStyle}>
            Exact camera choices: {exactValues.join(", ")}
          </div>
        ) : (
          disabled && reason && <div style={hintStyle}>{reason}</div>
        )}
      </div>
      <div style={rangeLineStyle}>
        <span style={limitStyle}>{min}</span>
        <input
          aria-label={`${label} slider`}
          type="range"
          min={usesExactValues ? 0 : min}
          max={usesExactValues ? exactValues.length - 1 : max}
          step={usesExactValues ? 1 : step}
          value={usesExactValues ? selectedIndex : value}
          disabled={disabled}
          onChange={(event) =>
            onChange(
              usesExactValues
                ? exactValues[Number(event.target.value)]
                : Number(event.target.value),
            )
          }
          style={{ width: "100%" }}
        />
        <span style={limitStyle}>{max}</span>
        {usesExactValues ? (
          <select
            aria-label={label}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(Number(event.target.value))}
            style={numberStyle}
          >
            {exactValues.map((candidate) => (
              <option key={candidate} value={candidate}>
                {candidate}
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label={label}
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(Number(event.target.value))}
            style={numberStyle}
          />
        )}
        <span style={{ color: "#64748b", fontSize: 13 }}>{unit}</span>
      </div>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div style={summaryStyle}>
      <span style={summaryLabelStyle}>{label}</span>
      <strong style={{ color: "#0f172a" }}>{value}</strong>
    </div>
  );
}

const panelStyle: React.CSSProperties = { display: "grid", gap: 16 };
const bannerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  padding: "15px 18px",
  border: "1px solid #bfdbfe",
  borderRadius: 14,
  background: "#eff6ff",
};
const planLockStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "14px 16px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  background: "#f1f5f9",
  color: "#64748b",
  fontSize: 12,
  lineHeight: 1.5,
};
const lockedBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: "5px 9px",
  border: "1px solid #cbd5e1",
  borderRadius: 999,
  background: "#e2e8f0",
  color: "#475569",
  fontSize: 11,
  fontWeight: 800,
};
const formStyle: React.CSSProperties = {
  display: "grid",
  minInlineSize: 0,
  margin: 0,
  padding: "12px 20px",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  background: "#ffffff",
};
const lockedFormStyle: React.CSSProperties = {
  borderColor: "#cbd5e1",
  background: "#f1f5f9",
  opacity: 0.78,
  cursor: "not-allowed",
};
const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "170px minmax(0, 1fr)",
  gap: 14,
  alignItems: "center",
  padding: "14px 0",
  borderBottom: "1px solid #e2e8f0",
};
const labelStyle: React.CSSProperties = { color: "#475569", fontSize: 13 };
const hintStyle: React.CSSProperties = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 11,
  lineHeight: 1.4,
};
const selectStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 520,
  minHeight: 44,
  padding: "9px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#ffffff",
  color: "#0f172a",
  font: "inherit",
};
const rangeLineStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "42px minmax(160px, 1fr) 52px 90px auto",
  gap: 10,
  alignItems: "center",
};
const limitStyle: React.CSSProperties = {
  color: "#2563eb",
  fontSize: 12,
  fontWeight: 800,
};
const numberStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 40,
  padding: "7px 9px",
  border: "1px solid #cbd5e1",
  borderRadius: 9,
};
const secondaryButtonStyle: React.CSSProperties = {
  minHeight: 40,
  padding: "8px 13px",
  border: "1px solid #93c5fd",
  borderRadius: 10,
  background: "#ffffff",
  color: "#1d4ed8",
  fontWeight: 800,
  cursor: "pointer",
};
const summaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
  paddingTop: 16,
};
const summaryStyle: React.CSSProperties = {
  display: "grid",
  gap: 5,
  padding: 13,
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  background: "#f8fafc",
};
const summaryLabelStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: 10,
  fontWeight: 800,
  textTransform: "uppercase",
};
const warningStyle: React.CSSProperties = {
  marginTop: 14,
  padding: 12,
  border: "1px solid #fde68a",
  borderRadius: 10,
  background: "#fffbeb",
  color: "#92400e",
  fontWeight: 700,
};
const noteStyle: React.CSSProperties = {
  marginTop: 12,
  color: "#64748b",
  fontSize: 12,
};
const streamUriStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 10,
  borderRadius: 9,
  background: "#f1f5f9",
  color: "#475569",
  fontFamily: "monospace",
  fontSize: 11,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const emptyStyle: React.CSSProperties = {
  padding: 22,
  border: "1px dashed #cbd5e1",
  borderRadius: 12,
  textAlign: "center",
  color: "#64748b",
};
function messageStyle(isError: boolean): React.CSSProperties {
  return {
    padding: 11,
    border: `1px solid ${isError ? "#fecaca" : "#bbf7d0"}`,
    borderRadius: 10,
    background: isError ? "#fef2f2" : "#f0fdf4",
    color: isError ? "#b91c1c" : "#166534",
    fontWeight: 700,
  };
}
function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    minHeight: 42,
    padding: "10px 16px",
    border: 0,
    borderRadius: 10,
    color: "#ffffff",
    background: "#111827",
    fontWeight: 800,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
